import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { logout } from "../../LoginPage/redux/auth.actions"
import {
  DEFAULT_DISPUTE_COLUMNS,
  DEFAULT_VISIBLE_FILTERS
} from "../pages/AllPage/constants/columns"
import { DISPUTES_SLICE_NAME as SLICE_NAME } from "./disputes.constants"
import {
  assignDispute,
  getDisputes,
  getDisputesExport,
  getDisputesStatistics,
  getReasonCategories,
  getTeamMembers,
  pinDispute,
  rejectEvidence,
  reopenDispute,
  syncDisputes
} from "./disputes.thunks"

/* ------------- Initial State ------------- */

export const DEFAULT_FILTERS = {
  status: [],
  // Case type: dispute | inquiry (see TYPE_LABELS in constants/statusMeta.js).
  dispute_type: [],
  // Reason filter is now a list of normalized categories (Stripe taxonomy),
  // not raw codes. One filter pill covers Visa 10.4 + Mastercard 4837 +
  // PayPal 'UNAUTHORISED' etc when set to 'fraudulent'.
  reason_category: [],
  reason_code: [],
  // Why a dispute is held for manual review (see FLAG_REASON_META). Backend param: flag.
  flag_reason: [],
  // Network lifecycle stage (first_cycle | pre_arbitration | arbitration). Backend param: stage.
  cycle_stage: [],
  issuer: [],
  issuing_bank: [],
  customer: [],
  // Free-text customer email search (comma-separated), the Customer email column's chip.
  customer_email: [],
  // Team-member ids the dispute is assigned to (or 'unassigned'). Backend param: assigned_to.
  assignee: [],
  card_type: [],
  currency: [],
  country: [],
  processor: [],
  card_last4: [],
  processor_dispute_id: [],
  date_from: null,
  date_to: null,
  // Last-update (updated_at) range, set by the "Last update" date filter. ISO date
  // strings or null. Separate from date_from/date_to (dispute-created date).
  updated_from: null,
  updated_to: null,
  // Response-deadline (due_by) range, set by the Analytics Action Center deep-links
  // (e.g. due within 48 hours / due by Friday). Separate from date_from/date_to,
  // which filter the dispute-created date. ISO datetime strings or null.
  due_from: null,
  due_to: null,
  // Transaction-date range, set by the "Transaction date" filter. Separate from
  // date_from/date_to (dispute-created date). ISO date strings or null.
  transaction_from: null,
  transaction_to: null,
  // Amount condition: { type: is_equal_to|is_between|is_greater_than|is_less_than,
  // min, max } (the DropdownBlockNumber shape), or null for no amount filter.
  amount: null,
  page: 1,
  page_size: 50
}

export const DEFAULT_STATISTICS = {
  all: 0,
  in_progress: 0,
  needs_response: 0,
  awaiting_review: 0,
  won: 0,
  lost: 0,
  lost_defended: 0,
  lost_undefended: 0,
  escalated: 0,
  pending_decision: 0,
  skipped: 0,
  won_amount: 0
}

const initialState = {
  list: {
    results: [],
    count: 0,
    // Counts for the CURRENT filter set — the list response computes them within its
    // own filters, so these shrink as the table is filtered. The All-page cards read
    // this slot.
    statistics: DEFAULT_STATISTICS,
    // Account-wide counts, written only by the stats-only refresh (which sends no
    // filters). The Sidebar badge reads this so filtering the table never shrinks it.
    accountStatistics: DEFAULT_STATISTICS,
    statisticsLoaded: false,
    fetching: false,
    exporting: false,
    syncing: false,
    error: null,
    // Id of the in-flight list request. The saga used takeLatest, which cancelled the
    // previous fetch; thunks aren't cancelled, so a late response from a superseded
    // request is dropped by comparing this instead.
    requestId: null
  },
  // Invited team members (for the Assignee column / detail control / auto-assign).
  team: { members: [], fetching: false, loaded: false },
  // Detail-page terminal action (reject/reopen) status. `lastResult` carries the most
  // recent outcome so the detail page can run its component-scoped follow-up (toast +
  // reload) reactively. Nulled on each new request so the effect fires once.
  detailAction: { acting: false, lastResult: null },
  filters: { ...DEFAULT_FILTERS },
  // Unified reason-category taxonomy (Stripe-aligned, 14 entries). Hydrated
  // from GET /recover/reason_categories/ on first use of any surface that
  // needs the dropdown (Preferences per-category rules, AllPage filter).
  reasonCategories: { options: [], fetching: false, error: null },
  // All-table prefs: column visibility/order + list vs grid view. A pinTick
  // increments on a successful pin so the page can refetch (pin reorders rows).
  table: {
    columns: DEFAULT_DISPUTE_COLUMNS,
    viewMode: "table", // 'table' | 'cards'
    visibleFilters: DEFAULT_VISIBLE_FILTERS, // which filter chips are shown
    pinTick: 0
  }
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setFilters(state, { payload }) {
      state.filters = { ...state.filters, ...(payload ?? {}) }
    },
    resetFilters(state) {
      state.filters = { ...DEFAULT_FILTERS }
    },
    setDisputeColumns(state, { payload }) {
      state.table.columns = payload || []
    },
    setDisputeViewMode(state, { payload }) {
      state.table.viewMode = payload || "table"
    },
    setDisputeVisibleFilters(state, { payload }) {
      state.table.visibleFilters = payload || DEFAULT_VISIBLE_FILTERS
    }
  },
  extraReducers: (builder) => {
    builder
      // Signing out clears the slice, via the shared LOGOUT action every other slice
      // resets on (LoginPage/redux/auth.actions).
      .addCase(logout, () => initialState)
      // getDisputes
      .addCase(getDisputes.pending, (state, { meta }) => {
        state.list.fetching = true
        state.list.error = null
        state.list.requestId = meta.requestId
      })
      // The inlined statistics are computed WITHIN the list filters, so they go to
      // `statistics` only. `accountStatistics` is left alone — the sidebar badge counts
      // the whole account, not the filtered page.
      .addCase(getDisputes.fulfilled, (state, { payload, meta }) => {
        if (state.list.requestId !== meta.requestId) return
        state.list.results = payload?.results ?? []
        state.list.count = payload?.count ?? 0
        state.list.statistics = { ...DEFAULT_STATISTICS, ...(payload?.statistics ?? {}) }
        state.list.fetching = false
        state.list.error = null
        state.list.requestId = null
      })
      .addCase(getDisputes.rejected, (state, { payload, meta }) => {
        if (state.list.requestId !== meta.requestId) return
        state.list.fetching = false
        state.list.error = payload ?? null
        state.list.requestId = null
      })

      // getDisputesStatistics — the stats-only refresh (sidebar badge) sends no filters,
      // so this is the account-wide count. It lands in BOTH slots: `accountStatistics`
      // for the badge (which must never follow the table's filters) and `statistics` as
      // the starting value for the cards, which the next list response then narrows.
      .addCase(getDisputesStatistics.fulfilled, (state, { payload }) => {
        const stats = { ...DEFAULT_STATISTICS, ...(payload?.statistics ?? {}) }
        state.list.statistics = stats
        state.list.accountStatistics = stats
        state.list.statisticsLoaded = true
      })
      // Mark loaded even on failure so the deactivated-shell can stop waiting and fall
      // back to the empty state rather than spinning forever.
      .addCase(getDisputesStatistics.rejected, (state) => {
        state.list.statisticsLoaded = true
      })

      // getDisputesExport
      .addCase(getDisputesExport.pending, (state) => {
        state.list.exporting = true
      })
      .addCase(getDisputesExport.fulfilled, (state) => {
        state.list.exporting = false
      })
      .addCase(getDisputesExport.rejected, (state) => {
        state.list.exporting = false
      })

      // syncDisputes
      .addCase(syncDisputes.pending, (state) => {
        state.list.syncing = true
      })
      .addCase(syncDisputes.fulfilled, (state) => {
        state.list.syncing = false
      })
      .addCase(syncDisputes.rejected, (state) => {
        state.list.syncing = false
      })

      // getTeamMembers
      .addCase(getTeamMembers.pending, (state) => {
        state.team.fetching = true
      })
      .addCase(getTeamMembers.fulfilled, (state, { payload }) => {
        state.team = { members: payload?.members ?? [], fetching: false, loaded: true }
      })
      .addCase(getTeamMembers.rejected, (state) => {
        state.team.fetching = false
        state.team.loaded = true
      })

      // getReasonCategories
      .addCase(getReasonCategories.pending, (state) => {
        state.reasonCategories.fetching = true
        state.reasonCategories.error = null
      })
      .addCase(getReasonCategories.fulfilled, (state, { payload }) => {
        state.reasonCategories = {
          options: payload?.options ?? [],
          fetching: false,
          error: null
        }
      })
      .addCase(getReasonCategories.rejected, (state, { payload }) => {
        state.reasonCategories.fetching = false
        state.reasonCategories.error = payload ?? null
      })

      // pinDispute — optimistic on pending, rolled back on rejected. Pin reorders the
      // list (pinned float to top), so a success bumps pinTick to trigger a refetch.
      .addCase(pinDispute.pending, (state, { meta }) => {
        state.list.results = (state.list.results || []).map((r) =>
          r.id === meta.arg?.id ? { ...r, pinned: !!meta.arg.pin } : r
        )
      })
      .addCase(pinDispute.fulfilled, (state) => {
        state.table.pinTick = (state.table.pinTick || 0) + 1
      })
      .addCase(pinDispute.rejected, (state, { meta }) => {
        state.list.results = (state.list.results || []).map((r) =>
          r.id === meta.arg?.id ? { ...r, pinned: !meta.arg.pin } : r
        )
      })

      // assignDispute — optimistic assign: set the row's assigned_to immediately, roll
      // back to `prev` on failure (the thunk toasts either way).
      .addCase(assignDispute.pending, (state, { meta }) => {
        state.list.results = (state.list.results || []).map((r) =>
          r.id === meta.arg?.id ? { ...r, assigned_to: meta.arg?.member ?? null } : r
        )
      })
      .addCase(assignDispute.rejected, (state, { meta }) => {
        state.list.results = (state.list.results || []).map((r) =>
          r.id === meta.arg?.id ? { ...r, assigned_to: meta.arg?.prev ?? null } : r
        )
        // Surface the failure so the detail page (which keeps its own local copy of the
        // dispute) can roll its rail's optimistic assignee back to `prev` too.
        state.detailAction.lastResult = {
          verb: "assign",
          ok: false,
          id: meta.arg?.id,
          prev: meta.arg?.prev ?? null
        }
      })

      // rejectEvidence
      .addCase(rejectEvidence.pending, (state) => {
        state.detailAction = { acting: true, lastResult: null }
      })
      .addCase(rejectEvidence.fulfilled, (state, { payload }) => {
        state.detailAction = {
          acting: false,
          lastResult: { verb: "reject", ok: true, data: payload?.data ?? null }
        }
      })
      .addCase(rejectEvidence.rejected, (state) => {
        state.detailAction = { acting: false, lastResult: { verb: "reject", ok: false } }
      })

      // reopenDispute
      .addCase(reopenDispute.pending, (state) => {
        state.detailAction = { acting: true, lastResult: null }
      })
      .addCase(reopenDispute.fulfilled, (state, { payload }) => {
        state.detailAction = {
          acting: false,
          lastResult: { verb: "reopen", ok: true, data: payload?.data ?? null }
        }
      })
      .addCase(reopenDispute.rejected, (state, { payload }) => {
        state.detailAction = {
          acting: false,
          lastResult: { verb: "reopen", ok: false, data: payload ?? null }
        }
      })
  }
})

export const {
  setFilters,
  resetFilters,
  setDisputeColumns,
  setDisputeViewMode,
  setDisputeVisibleFilters
} = slice.actions

/* ------------- Selectors ------------- */

export const selectList = (state) => state.disputes.list
export const selectFilters = (state) => state.disputes.filters
export const selectTable = (state) => state.disputes.table
export const selectTableColumns = (state) => state.disputes.table.columns
export const selectDetailAction = (state) => state.disputes.detailAction
export const selectTeamMembers = (state) => state.disputes.team.members
export const selectReasonCategoryOptions = (state) => state.disputes.reasonCategories.options
export const selectStatistics = (state) => state.disputes.list.statistics
export const selectAccountStatistics = (state) => state.disputes.list.accountStatistics

/* ------------- Persistence ------------- */
// `list`, `team`, `reasonCategories` and `detailAction` are server-owned and refetched
// on every mount; persisting them would paint stale rows and counts before the
// authoritative fetch resolves. The merchant's own table prefs and filters do persist.

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "disputesThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["list", "team", "reasonCategories", "detailAction"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
